/**
 * Geofence-exit departure prompt (pure, shared by the frontend store and the
 * update-session-state lambda).
 *
 * Owner decision after the 2026-10-08 field test: the provider drove off
 * without recording departure, so the in-session GPS track absorbed the
 * drive home AND the departure leg was added as "assumed, same as arrival"
 * — the same kilometres twice. While a session is checked in, the device
 * watches the position stream (whatever stream exists — foreground today,
 * background later; this module never sources positions) and, once the
 * provider has clearly left, asks: "Looks like you've left — record your
 * departure?". On confirm the departure time is the moment they LEFT (the
 * first qualifying fix outside the geofence), not the moment they tapped,
 * and GPS after that moment belongs to the departure leg.
 *
 * THRESHOLDS (hysteresis around the 150 m arrival geofence):
 *  - a fix only counts when its reported accuracy is ≤
 *    {@link GEOFENCE_EXIT_MAX_ACCURACY_METERS} (coarse fixes never trigger);
 *  - an EXCURSION starts at the first fix further than
 *    {@link GEOFENCE_EXIT_RADIUS_METERS} (250 m) from the session address;
 *  - it is CONFIRMED (→ `exitedAt`) once at least
 *    {@link GEOFENCE_EXIT_MIN_FIXES} qualifying outside fixes span at least
 *    {@link GEOFENCE_EXIT_MIN_DURATION_MS} — a single GPS wobble cannot ask;
 *  - a fix back inside {@link GEOFENCE_REENTRY_RADIUS_METERS} (150 m, the
 *    arrival geofence) ends the excursion and RE-ARMS the prompt;
 *  - fixes in the 150–250 m band change nothing (hysteresis).
 *
 * 2026-10-19 field test (build 127) — the prompt fired ~1 minute after
 * check-in because the provider checked in 266 m from the pin and every fix
 * after was >250 m away ("just because I'm moving away from the school it
 * thinks I've finished"). Owner rule: the LIVE prompt only fires once the
 * provider was SEEN INSIDE the visit area — the address pin geofence (150 m)
 * OR within 150 m of their own check-in point — and has then been
 * continuously outside BOTH (>250 m) for
 * {@link LIVE_EXIT_PROMPT_MIN_OUTSIDE_MS} (5 min). The area is anchored at the
 * check-in point when that is far from the pin. The prompt is off entirely
 * for session types that legitimately travel (see
 * `departure-prompt-policy.ts`). The check-out SUGGESTION and the server's
 * `departedAt` validation use the same inside-first anchor rule.
 *
 * The prompt is asked ONCE per excursion (`prompted`); dismissing it ("I'm
 * still here" — e.g. a community-access drive during the visit) keeps the
 * session running and nothing asks again until the provider comes back
 * inside and leaves again.
 */

import { ARRIVAL_GEOFENCE_RADIUS_METERS, ARRIVAL_MAX_ACCURACY_METERS } from './journey'
import { haversineKm } from './km'
import type { DriveSegment, GeoPoint, GeoStamp } from './types'

/** Outside this distance from the address a fix counts toward an exit. */
export const GEOFENCE_EXIT_RADIUS_METERS = 250
/** Back inside this distance the excursion ends and the prompt re-arms. */
export const GEOFENCE_REENTRY_RADIUS_METERS = ARRIVAL_GEOFENCE_RADIUS_METERS
/** Fixes coarser than this never count (same bar as auto-arrival). */
export const GEOFENCE_EXIT_MAX_ACCURACY_METERS = ARRIVAL_MAX_ACCURACY_METERS
/** Sustained-exit requirement: at least this many qualifying outside fixes… */
export const GEOFENCE_EXIT_MIN_FIXES = 2
/** …spanning at least this long. */
export const GEOFENCE_EXIT_MIN_DURATION_MS = 60_000
/**
 * LIVE prompt only: how long the provider must be CONTINUOUSLY outside the
 * visit area (after being seen inside) before "Looks like you've left" asks.
 * Longer than the check-out suggestion's bar so a drive to the shops during a
 * visit, or leaving the car park, never interrupts the session.
 */
export const LIVE_EXIT_PROMPT_MIN_OUTSIDE_MS = 5 * 60_000

export interface GeofenceExitState {
  /**
   * A qualifying fix has been seen INSIDE the visit area (pin geofence or the
   * provider's own check-in point) since check-in / the last undo. No excursion
   * is tracked, and so no prompt fires, until this is true. Absent on state
   * persisted by older builds → treated as false.
   */
  insideSeen?: boolean
  /** Timestamp of the first qualifying outside fix of the current excursion. */
  firstOutsideAt: number | null
  /** Qualifying outside fixes seen in the current excursion. */
  outsideFixCount: number
  /**
   * Confirmed departure moment (= `firstOutsideAt` once the excursion is
   * sustained). Null while inside or not yet sustained.
   */
  exitedAt: number | null
  /** The prompt for this excursion has been shown (and answered or dismissed). */
  prompted: boolean
  /**
   * When the provider answered "I'm still here" for this excursion (epoch
   * ms). Sent with the check-out departure preview so the server never
   * suggests an exit the provider already said wasn't one.
   */
  dismissedAt?: number | null
}

export const INITIAL_GEOFENCE_EXIT_STATE: GeofenceExitState = {
  insideSeen: false,
  firstOutsideAt: null,
  outsideFixCount: 0,
  exitedAt: null,
  prompted: false,
  dismissedAt: null,
}

/** Does a fix qualify for exit/re-entry decisions at all? */
function qualifies(fix: GeoStamp): boolean {
  const accuracy = fix.accuracy
  if (accuracy == null) return false
  if (!Number.isFinite(accuracy) || accuracy < 0) return false
  return accuracy <= GEOFENCE_EXIT_MAX_ACCURACY_METERS
}

/** Distances (m) from the pin and from the check-in point; Infinity when absent. */
function areaDistances(
  fix: GeoStamp,
  sessionGeo: GeoPoint | null | undefined,
  checkInPoint: GeoPoint | null | undefined,
): { pin: number; checkIn: number } {
  return {
    pin: sessionGeo ? haversineKm(fix, sessionGeo) * 1000 : Number.POSITIVE_INFINITY,
    checkIn: checkInPoint ? haversineKm(fix, checkInPoint) * 1000 : Number.POSITIVE_INFINITY,
  }
}

/**
 * Initial live-prompt state for a fresh check-in. The check-in fix itself is
 * the first sighting INSIDE the visit area (its own 150 m), so the area is
 * anchored at the check-in point even when that is far from the pin. A
 * check-in without a usable fix seeds nothing: the provider must then be seen
 * inside the pin geofence first.
 */
export function seedGeofenceExitState(checkIn: GeoStamp | null | undefined): GeofenceExitState {
  return checkIn && qualifies(checkIn)
    ? { ...INITIAL_GEOFENCE_EXIT_STATE, insideSeen: true }
    : INITIAL_GEOFENCE_EXIT_STATE
}

/**
 * Fold one position fix into the exit state. Pure — returns the same object
 * when nothing changed so subscribers can short-circuit.
 *
 * The visit AREA is the pin geofence ∪ a geofence around `checkInPoint`
 * (the check-in point anchors it when the provider checked in away from the
 * pin). Inside = ≤150 m of either; an excursion = >250 m from BOTH, and only
 * tracked once an inside fix has been seen ({@link GeofenceExitState.insideSeen}).
 */
export function advanceGeofenceExit(
  state: GeofenceExitState,
  fix: GeoStamp,
  sessionGeo: GeoPoint | null | undefined,
  checkInPoint?: GeoPoint | null,
): GeofenceExitState {
  if ((!sessionGeo && !checkInPoint) || !qualifies(fix)) return state
  const { pin, checkIn } = areaDistances(fix, sessionGeo, checkInPoint)

  if (pin <= GEOFENCE_REENTRY_RADIUS_METERS || checkIn <= GEOFENCE_REENTRY_RADIUS_METERS) {
    // Inside the visit area: end any excursion, re-arm, and remember we were here.
    if (
      state.insideSeen === true &&
      state.firstOutsideAt == null &&
      state.outsideFixCount === 0 &&
      state.exitedAt == null &&
      !state.prompted
    ) {
      return state
    }
    return { ...INITIAL_GEOFENCE_EXIT_STATE, insideSeen: true }
  }

  // Hysteresis band (near either anchor): changes nothing.
  if (pin <= GEOFENCE_EXIT_RADIUS_METERS || checkIn <= GEOFENCE_EXIT_RADIUS_METERS) return state

  // Never seen inside: moving away from the check-in point is not a departure
  // (the provider may simply be arriving / driving to the visit).
  if (state.insideSeen !== true) return state

  const firstOutsideAt = state.firstOutsideAt ?? fix.timestamp
  const outsideFixCount = state.outsideFixCount + 1
  const sustained =
    outsideFixCount >= GEOFENCE_EXIT_MIN_FIXES &&
    fix.timestamp - firstOutsideAt >= LIVE_EXIT_PROMPT_MIN_OUTSIDE_MS
  return {
    ...state,
    firstOutsideAt,
    outsideFixCount,
    exitedAt: state.exitedAt ?? (sustained ? firstOutsideAt : null),
  }
}

/** The excursion is confirmed and the provider has not been asked yet. */
export function shouldPromptGeofenceExit(state: GeofenceExitState | null | undefined): boolean {
  return Boolean(state && state.exitedAt != null && !state.prompted)
}

/** Mark the current excursion's prompt as shown/answered (no re-ask until re-entry). */
export function markGeofenceExitPrompted(state: GeofenceExitState): GeofenceExitState {
  if (state.prompted) return state
  return { ...state, prompted: true }
}

/** "I'm still here": answered, and remembered for the check-out preview. */
export function markGeofenceExitDismissed(state: GeofenceExitState, at: number): GeofenceExitState {
  return { ...state, prompted: true, dismissedAt: at }
}

/* ------------------------- server-side validation ------------------------- */

/**
 * How far before the last in-geofence fix a backdated departure may land.
 * The client's `departedAt` is the FIRST outside fix, which by construction
 * follows the last inside fix; a small tolerance absorbs clock skew and the
 * segment/ping cadence.
 */
export const DEPARTED_AT_TOLERANCE_MS = 2 * 60_000
/** A departure time this far in the future is a clock problem — reject. */
export const DEPARTED_AT_FUTURE_SKEW_MS = 60_000

export type DepartedAtValidation =
  | { ok: true; actualEnd: number }
  | {
      ok: false
      reason:
        | 'not-finite'
        | 'before-arrival'
        | 'in-future'
        | 'before-last-inside-fix'
        | 'no-inside-fix'
    }

/** Flatten segments into their ordered sample list. */
function samplesOf(segments: readonly DriveSegment[]): GeoStamp[] {
  if (segments.length === 0) return []
  const samples: GeoStamp[] = [segments[0].from]
  for (const segment of segments) samples.push(segment.to)
  return samples
}

/**
 * Server-side check of a client-supplied geofence-exit departure time
 * (update-session-state `check-out` with `departedAt`). The record itself
 * never fails on this — a rejected value falls back to the normal
 * fix/occurredAt time; this only decides whether the BACKDATE is credible:
 *
 *  - finite;
 *  - not before the recorded arrival (`actualStart`);
 *  - not in the future (beyond {@link DEPARTED_AT_FUTURE_SKEW_MS});
 *  - not earlier than the last qualifying fix INSIDE the exit radius in the
 *    submitted track by more than {@link DEPARTED_AT_TOLERANCE_MS} — the
 *    provider cannot claim to have left while the GPS still had them at
 *    the address (or their check-in point);
 *  - backed by AT LEAST ONE qualifying fix inside the visit area (pin
 *    geofence ≤150 m or check-in point ≤150 m — the check-in fix counts). A
 *    track that never saw the provider inside cannot prove they ever left
 *    (`no-inside-fix`): the backdate is rejected and the tap time is used,
 *    so a zero-length visit can never be recorded.
 */
export function validateDepartedAt(input: {
  departedAt: unknown
  actualStart: number | null | undefined
  now: number
  segments: readonly DriveSegment[] | null | undefined
  sessionGeo: GeoPoint | null | undefined
  /** The provider's recorded check-in fix — anchors the visit area when away from the pin. */
  checkIn?: GeoStamp | null
}): DepartedAtValidation {
  const { departedAt, actualStart, now, segments, sessionGeo, checkIn } = input
  if (typeof departedAt !== 'number' || !Number.isFinite(departedAt)) {
    return { ok: false, reason: 'not-finite' }
  }
  if (actualStart != null && departedAt < actualStart) {
    return { ok: false, reason: 'before-arrival' }
  }
  if (departedAt > now + DEPARTED_AT_FUTURE_SKEW_MS) {
    return { ok: false, reason: 'in-future' }
  }
  const checkInPoint = checkIn && qualifies(checkIn) ? checkIn : null
  const samples = samplesOf(segments ?? [])
  if (checkInPoint) samples.push(checkInPoint)
  let lastInsideAt: number | null = null
  let everInside = false
  for (const sample of samples) {
    if (!qualifies(sample)) continue
    const { pin, checkIn: fromCheckIn } = areaDistances(sample, sessionGeo, checkInPoint)
    if (pin <= GEOFENCE_REENTRY_RADIUS_METERS || fromCheckIn <= GEOFENCE_REENTRY_RADIUS_METERS) {
      everInside = true
    }
    if (pin <= GEOFENCE_EXIT_RADIUS_METERS || fromCheckIn <= GEOFENCE_EXIT_RADIUS_METERS) {
      lastInsideAt = Math.max(lastInsideAt ?? 0, sample.timestamp)
    }
  }
  if (!everInside) return { ok: false, reason: 'no-inside-fix' }
  if (lastInsideAt != null && departedAt < lastInsideAt - DEPARTED_AT_TOLERANCE_MS) {
    return { ok: false, reason: 'before-last-inside-fix' }
  }
  return { ok: true, actualEnd: departedAt }
}

/* ------------------ check-out departure SUGGESTION ------------------ */

/**
 * A suggested departure time is only offered when it is at least this far
 * before the check-out tap — anything closer is just the walk to the car.
 */
export const DEPARTURE_SUGGESTION_MIN_LEAD_MS = 2 * 60_000

export type TrackDepartureInference =
  | { ok: true; exitedAt: number; outsideFixCount: number }
  | {
      ok: false
      reason:
        | 'no-geo'
        | 'remote-arrival'
        | 'no-track'
        | 'not-outside-at-check-out'
        | 'not-sustained'
        | 'no-inside-anchor'
        | 'dismissed'
        | 'too-recent'
        | Extract<DepartedAtValidation, { ok: false }>['reason']
    }

/**
 * Did the provider leave the address BEFORE tapping check-out? Answers the
 * check-out departure PREVIEW only (update-session-state
 * `previewDeparture: true`). The result is a SUGGESTION the provider confirms
 * or declines on a sheet — it never changes billed time or km by itself
 * (coordinator decision 2026-10-15, after review of the auto-backdate:
 * community outings that end away from home and check-ins away from the pin
 * must never silently lose visit time).
 *
 * Looks at the TRAILING run of accuracy-qualified, non-mock fixes (arrival →
 * check-out track, plus the check-out fix at its clamped tap time) further
 * than {@link GEOFENCE_EXIT_RADIUS_METERS} from the address. As with the live
 * prompt, only a fix back within {@link GEOFENCE_REENTRY_RADIUS_METERS} ends a
 * run (the band between changes nothing), so an out-and-back drive is never mistaken for the
 * departure. No suggestion when:
 *  - the arrival was flagged remote (`arrivalLocationFlag`);
 *  - the run is not sustained ({@link GEOFENCE_EXIT_MIN_FIXES} fixes over
 *    {@link GEOFENCE_EXIT_MIN_DURATION_MS});
 *  - the track never put the provider within
 *    {@link GEOFENCE_REENTRY_RADIUS_METERS} of the address OR of their own
 *    check-in point (`no-inside-anchor`; the check-in fix itself counts, so a
 *    check-in away from the pin anchors the area at the check-in point instead
 *    of "exiting" one fix after arrival);
 *  - the provider answered "I'm still here" during the run (`exitDismissedAt`);
 *  - the exit fails {@link validateDepartedAt}, or is less than
 *    {@link DEPARTURE_SUGGESTION_MIN_LEAD_MS} before the tap.
 * `exitedAt` is the run's first fix — the moment the prompt would use.
 */
export function inferDepartureFromTrack(input: {
  segments: readonly DriveSegment[] | null | undefined
  checkOutLocation?: GeoStamp | null
  /** The clamped tap time — the check-out fix is evaluated at THIS time. */
  checkOutAt: number
  sessionGeo: GeoPoint | null | undefined
  actualStart: number | null | undefined
  arrivalFlagged?: boolean
  exitDismissedAt?: number | null
  /** The provider's check-in fix — anchors the visit area when checked in away from the pin. */
  checkIn?: GeoStamp | null
  now: number
}): TrackDepartureInference {
  const { segments, checkOutLocation, checkOutAt, sessionGeo, actualStart, now } = input
  if (!sessionGeo) return { ok: false, reason: 'no-geo' }
  const checkInPoint = input.checkIn && qualifies(input.checkIn) ? input.checkIn : null
  if (input.arrivalFlagged) return { ok: false, reason: 'remote-arrival' }
  const samples = samplesOf(segments ?? [])
  if (checkOutLocation) samples.push({ ...checkOutLocation, timestamp: checkOutAt })
  const qualifying = samples
    .filter(
      (s) =>
        qualifies(s) &&
        s.isMock !== true &&
        Number.isFinite(s.timestamp) &&
        (actualStart == null || s.timestamp >= actualStart),
    )
    .sort((a, b) => a.timestamp - b.timestamp)
  if (qualifying.length === 0) return { ok: false, reason: 'no-track' }

  let runStart: GeoStamp | null = null
  let runCount = 0
  // The check-in fix is itself a sighting inside the visit area (anchored at
  // the check-in point when that is far from the pin).
  let anchored = checkInPoint != null
  for (const sample of qualifying) {
    const { pin, checkIn: fromCheckIn } = areaDistances(sample, sessionGeo, checkInPoint)
    const nearest = Math.min(pin, fromCheckIn)
    if (nearest > GEOFENCE_EXIT_RADIUS_METERS) {
      runStart ??= sample
      runCount += 1
    } else if (nearest <= GEOFENCE_REENTRY_RADIUS_METERS) {
      // Back home ends the excursion (same hysteresis as the live prompt);
      // every such fix precedes the trailing run.
      anchored = true
      runStart = null
      runCount = 0
    }
    // 150–250 m band: changes nothing.
  }
  if (!runStart) return { ok: false, reason: 'not-outside-at-check-out' }
  const last = qualifying[qualifying.length - 1]
  if (runCount < GEOFENCE_EXIT_MIN_FIXES || last.timestamp - runStart.timestamp < GEOFENCE_EXIT_MIN_DURATION_MS) {
    return { ok: false, reason: 'not-sustained' }
  }
  if (!anchored) return { ok: false, reason: 'no-inside-anchor' }
  const dismissedAt = input.exitDismissedAt
  if (typeof dismissedAt === 'number' && Number.isFinite(dismissedAt) && dismissedAt >= runStart.timestamp) {
    return { ok: false, reason: 'dismissed' }
  }

  const verdict = validateDepartedAt({
    departedAt: runStart.timestamp,
    actualStart,
    now,
    segments,
    sessionGeo,
    checkIn: input.checkIn ?? null,
  })
  if (!verdict.ok) return { ok: false, reason: verdict.reason }
  if (verdict.actualEnd > checkOutAt - DEPARTURE_SUGGESTION_MIN_LEAD_MS) {
    return { ok: false, reason: 'too-recent' }
  }
  return { ok: true, exitedAt: verdict.actualEnd, outsideFixCount: runCount }
}
